import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl, getToken, request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import { useFmt } from '@/lib/useFmt.js';
import { GenerateForm } from './codes/GenerateForm.js';
import { GlassButton, PageHeader, Pagination, useConfirm } from '@/components/ui/index.js';
import type { CodeListResponse, RedeemCodeItem } from '@/lib/types.js';

const PAGE_SIZE = 50;
const PAGE_OPENED_AT = Date.now();

export function CodesPage(): JSX.Element {
  const confirm = useConfirm();
  const [codes, setCodes] = useState<RedeemCodeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [usedFilter, setUsedFilter] = useState<'all' | 'unused' | 'used'>('all');
  const [expirationFilter, setExpirationFilter] = useState<'all' | 'expired' | 'expiring_7d' | 'valid'>('all');
  const [batchFilter, setBatchFilter] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bulk operations (audit B7 / B10)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const { fmtDateTime } = useFmt();

  // Client-side expiration filter — applied after the backend page slice.
  // For V1.2 admin's code counts (< 5k), one page covers most cases; we'll
  // push this server-side when the listing pagination starts hiding matches.
  const visibleCodes = codes.filter((c) => matchesExpirationFilter(c.expires_at, expirationFilter));

  function toggleSelected(code: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function handleExportCsv(): Promise<void> {
    const params = new URLSearchParams();
    if (batchFilter !== '') params.set('batch_id', batchFilter);
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/admin/redeem_codes/export.csv?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken() ?? ''}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (res.headers.get('Content-Disposition') ?? '')
        .match(/filename="([^"]+)"/)?.[1] ?? 'unused-codes.csv';
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      useToast.getState().show('success', '已下载未用兑换码 CSV');
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof Error ? err.message : '导出失败',
      );
    }
  }

  async function handleBulkDelete(): Promise<void> {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: '删除兑换码',
      message:
        `确认删除选中的 ${selected.size} 个未使用兑换码？\n` +
        `（已被用过的码会自动跳过，不删除）\n` +
        `此操作不可撤销。`,
      confirmLabel: '删除',
      tone: 'danger',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await request<{ ok: true; deleted: number; skipped: number }>(
        '/admin/redeem_codes/bulk_delete',
        { method: 'POST', body: { codes: Array.from(selected) } },
      );
      useToast.getState().show(
        'success',
        `已删除 ${res.deleted} 个${res.skipped > 0 ? `（跳过 ${res.skipped} 个已使用）` : ''}`,
      );
      setSelected(new Set());
      await load(usedFilter, batchFilter, offset);
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof Error ? err.message : '删除失败',
      );
    } finally {
      setBulkBusy(false);
    }
  }

  const load = useCallback(
    async (used: typeof usedFilter, batch: string, offsetValue: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(offsetValue),
        });
        if (used === 'unused') params.set('used', 'false');
        if (used === 'used') params.set('used', 'true');
        if (batch !== '') params.set('batch_id', batch);
        const res = await request<CodeListResponse>(`/admin/redeem_codes?${params.toString()}`);
        setCodes(res.codes);
        setTotal(res.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(usedFilter, batchFilter, offset);
  }, [usedFilter, batchFilter, offset, load]);

  return (
    <div>
      <PageHeader title="兑换码" subtitle="生成 / 派发 / 查看激活记录。激活直接送会员（不抵扣 Stripe）。" />

      <GenerateForm
        onGenerated={async (): Promise<void> => {
          await load(usedFilter, batchFilter, 0);
          setOffset(0);
        }}
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select
          value={usedFilter}
          onChange={(e) => {
            setUsedFilter(e.target.value as typeof usedFilter);
            setOffset(0);
          }}
          className="px-3 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
        >
          <option value="all">全部状态</option>
          <option value="unused">未使用</option>
          <option value="used">已使用</option>
        </select>
        <select
          value={expirationFilter}
          onChange={(e): void => setExpirationFilter(e.target.value as typeof expirationFilter)}
          className="px-3 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
        >
          <option value="all">全部有效期</option>
          <option value="valid">未过期</option>
          <option value="expiring_7d">7 天内将过期</option>
          <option value="expired">已过期</option>
        </select>
        <input
          type="text"
          value={batchInput}
          onChange={(e) => setBatchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setBatchFilter(batchInput);
              setOffset(0);
            }
          }}
          placeholder="按批次过滤"
          className="w-full sm:w-48 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200 placeholder:text-slate-500 min-w-0"
        />
        <span className="sm:ml-auto text-sm text-slate-400">共 {total} 个</span>
        <GlassButton
          size="sm"
          variant="ghost"
          onClick={() => void handleExportCsv()}
        >
          📥 导出未用码 CSV
        </GlassButton>
      </div>

      {selected.size > 0 && (
        <div className="bg-shark-900/30 border border-shark-700/40 rounded-xl backdrop-blur-sm px-4 py-3 mb-4 flex items-center gap-3 sticky top-16 z-10">
          <span className="text-sm text-shark-200">
            已选中 <strong>{selected.size}</strong> 个兑换码
          </span>
          <GlassButton
            size="sm"
            variant="ghost"
            onClick={() => setSelected(new Set())}
          >
            取消选择
          </GlassButton>
          <div className="ml-auto">
            <GlassButton
              size="sm"
              variant="danger"
              disabled={bulkBusy}
              onClick={() => void handleBulkDelete()}
            >
              {bulkBusy ? '删除中…' : '🗑 批量删除（仅未使用）'}
            </GlassButton>
          </div>
        </div>
      )}

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="mat-matte rounded-xl overflow-hidden">
       <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-2 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">兑换码</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">套餐</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">批次</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">状态</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">有效期</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">生成时间</th>
            </tr>
          </thead>
          <tbody>
            {loading && codes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  加载中…
                </td>
              </tr>
            ) : codes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  暂无兑换码
                </td>
              </tr>
            ) : visibleCodes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  当前筛选下没有匹配兑换码
                </td>
              </tr>
            ) : (
              visibleCodes.map((c) => {
                const isUnused = c.used_by_user === null;
                return (
                  <tr key={c.code} className="border-t border-white/[0.08]">
                    <td className="px-2 py-3">
                      {isUnused ? (
                        <input
                          type="checkbox"
                          checked={selected.has(c.code)}
                          onChange={() => toggleSelected(c.code)}
                          className="accent-shark-500"
                        />
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-200">{c.code}</td>
                    <td className="px-4 py-3 text-slate-300">
                      {c.plan === 'monthly' ? '月卡' : '年卡'}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {c.batch_id ?? <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {!isUnused ? (
                        <span className="text-slate-500">
                          已使用
                          {c.used_at !== null && (
                            <span className="ml-2 text-xs">
                              {fmtDateTime(c.used_at)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-felt-400">未使用</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ExpirationCell expiresAt={c.expires_at} fmtDateTime={fmtDateTime} />
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {fmtDateTime(c.created_at)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
       </div>
      </div>

      <Pagination
        offset={offset}
        total={total}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

type ExpirationFilter = 'all' | 'expired' | 'expiring_7d' | 'valid';

function matchesExpirationFilter(expiresAt: string | null, filter: ExpirationFilter): boolean {
  if (filter === 'all') return true;
  if (expiresAt === null) {
    // Code with no explicit expiry is considered always-valid.
    return filter === 'valid';
  }
  const ms = new Date(expiresAt).getTime();
  if (!Number.isFinite(ms)) return true;
  const now = PAGE_OPENED_AT;
  if (filter === 'expired') return ms < now;
  if (filter === 'valid') return ms >= now;
  return ms >= now && ms < now + 7 * 24 * 60 * 60 * 1000;
}

function ExpirationCell({
  expiresAt,
  fmtDateTime,
}: {
  expiresAt: string | null;
  fmtDateTime: (s: string | null | undefined) => string;
}): JSX.Element {
  if (expiresAt === null) {
    return <span className="text-xs text-slate-500">无限期</span>;
  }
  const ms = new Date(expiresAt).getTime();
  if (!Number.isFinite(ms)) {
    return <span className="text-xs text-slate-600">—</span>;
  }
  const now = PAGE_OPENED_AT;
  if (ms < now) {
    return (
      <span className="text-xs text-rose-300">
        已过期 · {fmtDateTime(expiresAt)}
      </span>
    );
  }
  const daysLeft = Math.ceil((ms - now) / (24 * 60 * 60 * 1000));
  if (daysLeft <= 7) {
    return (
      <span className="text-xs text-amber-300">
        {daysLeft} 天后过期
      </span>
    );
  }
  return <span className="text-xs text-slate-400">{fmtDateTime(expiresAt)}</span>;
}
